"use client"

import type React from "react"
import { useSelector, useDispatch } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import type { RootState } from "../store/store"
import { removeFromCart, updateQuantity, clearCart } from "../store/slices/cartSlice"
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft } from "lucide-react"
import toast from "react-hot-toast"
import { useEffect } from "react"
import { userService } from "../services/userService"
import SEOHead from "../components/common/SEOHead"
import LazyImage from "../components/common/LazyImage"

// Helper to slugify strings for URLs
function slugify(str: string | undefined) {
  if (!str) return "";
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

const Cart: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { items, total, totalItems } = useSelector((state: RootState) => state.cart)
  const { user } = useSelector((state: RootState) => state.auth)

  useEffect(() => {
    if (user) {
      userService.updateCart(user.uid, items as any)
    }
  }, [items, user])

  const handleQuantityChange = (id: string, newQuantity: number) => {
    if (newQuantity < 1) return
    dispatch(updateQuantity({ id, quantity: newQuantity }))
  }

  const handleRemoveItem = (id: string) => {
    dispatch(removeFromCart(id))
    toast.success("Item removed from cart")
  }

  const handleClearCart = () => {
    dispatch(clearCart())
    toast.success("Cart cleared")
  }

  const handleCheckout = () => {
    if (!user) {
      toast.error("Please login to proceed to checkout")
      navigate("/login")
      return
    }
    navigate("/checkout")
  }

  if (items.length === 0) {
    return (
      <>
        <SEOHead
          title="Your Shopping Cart | Anmol Furniture Bazaar"
          description="Your shopping cart is empty. Add premium furniture to your cart and checkout easily."
          keywords="cart, shopping cart, buy furniture, premium furniture"
        />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <ShoppingBag className="w-24 h-24 text-gray-300 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
            <p className="text-gray-600 mb-8">Looks like you haven't added any items to your cart yet.</p>
            <Link
              to="/"
              className="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SEOHead
        title="Your Shopping Cart | Anmol Furniture Bazaar"
        description="View and manage your shopping cart at Anmol Furniture Bazaar. Checkout premium furniture easily."
        keywords="cart, shopping cart, buy furniture, premium furniture"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Shopping Cart",
          itemListElement: items.map((item, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            url: `https://anmolfurniture.com/products/${item.id}`,
            name: item.name,
            image: item.image,
            description: item.category,
          })),
        }}
      />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Shopping Cart</h1>
                <p className="text-gray-600">{totalItems} items in your cart</p>
              </div>
            </div>

            {items.length > 0 && (
              <button
                onClick={handleClearCart}
                className="text-red-600 hover:text-red-700 font-medium text-sm sm:text-base"
              >
                Clear Cart
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => (
                <div key={item.id} className="bg-white rounded-xl shadow-md p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                    {/* Product Image - Using LazyImage component */}
                    <Link
                      to={`/${slugify(item.category)}/${slugify(item.name)}`}
                      className="flex-shrink-0 w-full sm:w-auto"
                    >
                      <div className="w-full h-48 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-36 lg:h-36 rounded-lg overflow-hidden border border-gray-200">
                        <LazyImage
                          src={item.image || item.images?.[0] || ""}
                          alt={item.name || "Product"}
                          className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          width={144}
                          height={144}
                        />
                      </div>
                    </Link>

                    {/* Product Details - Responsive layout */}
                    <div className="flex-1 min-w-0 w-full sm:w-auto">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex-1 min-w-0 mb-3 sm:mb-0">
                          <Link
                            to={`/${slugify(item.category)}/${slugify(item.name)}`}
                            className="text-base sm:text-lg font-semibold text-gray-900 hover:text-primary-600 transition-colors line-clamp-2"
                          >
                            {item.name}
                          </Link>
                          <p className="text-sm text-gray-500 capitalize mt-1">{item.category}</p>

                          {item.variant && (
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              {item.variant.size && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md">
                                  Size: {item.variant.size}
                                </span>
                              )}
                              {item.variant.color && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md flex items-center gap-1">
                                  {item.variant.colorHex && (
                                    <span
                                      className="w-3 h-3 rounded-full border border-gray-300"
                                      style={{ backgroundColor: item.variant.colorHex }}
                                    />
                                  )}
                                  Color: {item.variant.color}
                                </span>
                              )}
                            </div>
                          )}

                          {(item as any).wardrobeVariant && (
                            <div className="mt-2">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md">
                                {(item as any).wardrobeVariant.isCustomSize ? (
                                  <>
                                    Custom Size: {(item as any).wardrobeVariant.customDimensions?.height}" ×{" "}
                                    {(item as any).wardrobeVariant.customDimensions?.width}"
                                  </>
                                ) : (
                                  <>Wardrobe: {(item as any).wardrobeVariant.name}</>
                                )}
                              </span>
                              {(item as any).wardrobeVariant.isCustomSize && (
                                <p className="text-xs text-gray-500 mt-1">Made to order - Custom dimensions</p>
                              )}
                            </div>
                          )}

                          <p className="text-lg sm:text-xl font-bold text-primary-600 mt-2">
                            ₹{item.price.toLocaleString()}
                          </p>
                        </div>

                        {/* Quantity Controls - Responsive positioning */}
                        <div className="flex items-center justify-between sm:justify-end space-x-3">
                          {!(item as any).wardrobeVariant && (
                            <div className="flex items-center border border-gray-300 rounded-lg">
                              <button
                                onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                                className="p-2 hover:bg-gray-100 transition-colors"
                                disabled={item.quantity <= 1}
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="px-3 sm:px-4 py-2 font-medium min-w-[3rem] text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                                className="p-2 hover:bg-gray-100 transition-colors"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          )}

                          {(item as any).wardrobeVariant && (
                            <div className="px-3 py-2 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-600">Qty: 1</span>
                            </div>
                          )}

                          {/* Remove Button */}
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Item Total - Responsive layout */}
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                    <span className="text-gray-600 text-sm sm:text-base">Subtotal:</span>
                    <span className="text-lg font-semibold text-gray-900">
                      ₹{(item.price * item.quantity).toLocaleString()}
                    </span>
                  </div>

                  {(item as any).wardrobeVariant && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Made to Order:</strong> This wardrobe will be custom manufactured according to your
                        specifications. Delivery time: 2-3 weeks from order confirmation.
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Order Summary - Sticky on larger screens */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 sticky top-8">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6">Order Summary</h3>

                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal ({totalItems} items)</span>
                    <span className="font-medium">₹{total.toLocaleString()}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-medium text-accent-600">Free</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax</span>
                    <span className="font-medium">₹{Math.round(total * 0.18).toLocaleString()}</span>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Total</span>
                      <span className="text-xl sm:text-2xl font-bold text-primary-600">
                        ₹{Math.round(total * 1.18).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {items.some((item) => (item as any).wardrobeVariant) && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Your cart contains made-to-order items. Delivery time may be extended to
                      2-3 weeks.
                    </p>
                  </div>
                )}

                {/* Coupon Code */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Coupon Code</label>
                  <div className="flex">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    />
                    <button className="px-3 sm:px-4 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg hover:bg-gray-200 transition-colors text-sm">
                      Apply
                    </button>
                  </div>
                </div>

                {/* Checkout Button */}
                <button
                  onClick={handleCheckout}
                  className="w-full mt-6 bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium text-base sm:text-lg"
                >
                  Proceed to Checkout
                </button>

                {/* Continue Shopping */}
                <Link
                  to="/"
                  className="block w-full mt-3 text-center py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm sm:text-base"
                >
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Cart
